import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';
import {
	Box,
	Button,
	Typography,
	TextField,
	MenuItem,
	Stack,
} from '@mui/material';
import inputSignalRulesService from '../../../../services/inputSignalRulesService';
import { useNotificationContext } from 'contexts/NotificationContext';

// Using upper case values here to match the value from selectedRule to avoid console warnings
// The LLM returns the value capitalized
const SEVERITY_OPTIONS = [
	{ label: 'Info', value: 'Info' },
	{ label: 'Warning', value: 'Warning' },
	{ label: 'Critical', value: 'Critical' },
];

// TODO - Add additional options when scope includes tenant level / shared alerts
const VISIBILTY_OPTIONS = [{ label: 'Personal', value: 'local' }];

const CustomizeReportAlertForm = ({ selectedAlert, reportId }) => {
	const navigate = useNavigate();
	const [title, setTitle] = useState(selectedAlert.title || '');
	const [severity, setSeverity] = useState(selectedAlert.severity || '');
	const [visibility, setVisibility] = useState(
		selectedAlert.visibility || 'local'
	);

	const { showNotification } = useNotificationContext();

	// Reset form fields when selectedAlert changes
	useEffect(() => {
		setTitle(selectedAlert.title || '');
		setSeverity(selectedAlert.severity || '');
		setVisibility(selectedAlert.visibility || 'local');
	}, [selectedAlert]);

	const handleAlertCreation = async () => {
		// Build params
		// BE handles automatic subscription to alert on creation
		const params = {
			report_id: reportId,
			// values from form:
			title: title.trim(),
			severity: severity.toLowerCase(),
			visibility: visibility,
			// values directly from selected rule:
			message: selectedAlert.message || '',
			category: selectedAlert.category || '',
			signal_type: selectedAlert.signal_type || '',
			metric: selectedAlert.metric,
			select: selectedAlert.select,
			target_kind: selectedAlert.target_kind || '',
		};

		try {
			await inputSignalRulesService.createInputSignalRule(params);
			showNotification('Alert created successfully', 'success');
			navigate('..'); // return user to parent route
		} catch (err) {
			console.error('Failed to create alert:', err);
			showNotification('Failed to create new alert', 'error');
		}
	};

	return (
		<>
			<Typography color='text.secondary' variant='overline'>
				Customize
			</Typography>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					handleAlertCreation();
				}}
			>
				<Box
					sx={{
						backgroundColor: 'background.paper',
						border: '0.5px solid',
						borderColor: 'border.light',
						borderRadius: '8px',
						padding: '12px 14px',
						cursor: 'pointer',
						transition: 'background-color 0.2s',
						overflow: 'auto',
						display: 'flex',
						py: 6,
						flexDirection: { xs: 'column', md: 'row' }, // switch to vertical layout on small screens
					}}
					gap={2}
					justifyContent='flex-start'
				>
					<Box flex={3}>
						<Stack>
							<TextField
								required
								label='Alert name'
								onChange={(e) => setTitle(e.target.value)}
								size='small'
								value={title}
								sx={{ width: '100%', minWidth: '200px' }}
							/>
						</Stack>
					</Box>

					<Box flex={1}>
						<Stack>
							<TextField
								data-testid='alert-severity-dropdown'
								select
								label='Severity'
								value={severity}
								onChange={(e) => setSeverity(e.target.value)}
								size='small'
								SelectProps={{
									renderValue: (selectedValue) => {
										const option = SEVERITY_OPTIONS.find(
											(opt) => opt.value === selectedValue
										);
										return option?.label || SEVERITY_OPTIONS[2].label;
									},
								}}
								sx={{ width: '100%' }}
							>
								{SEVERITY_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</TextField>
						</Stack>
					</Box>

					<Box flex={1}>
						<Stack>
							<TextField
								// All alerts are currently "Personal" ('local' on BE)
								// Disabled until entity level alerts are supported
								disabled={true}
								label='Visibility'
								data-testid='alert-visibility-dropdown'
								select
								value={visibility}
								onChange={(e) => setVisibility(e.target.value)}
								size='small'
								SelectProps={{
									renderValue: (selectedValue) => {
										const option = VISIBILTY_OPTIONS.find(
											(opt) => opt.value === selectedValue
										);
										return option?.label || 'Personal';
									},
								}}
								sx={{ width: '100%' }}
							>
								{VISIBILTY_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</TextField>
						</Stack>
					</Box>
				</Box>

				{/* Action buttons */}
				<Box
					display='flex'
					flexDirection='row'
					justifyContent='flex-end'
					sx={{ mt: 4 }}
					gap={2}
				>
					<Button
						onClick={() => navigate('..')}
						variant='outlined'
						sx={{
							border: '0.5px solid',
							borderColor: 'border.subtle',
						}}
					>
						Cancel
					</Button>
					<Button
						type='submit'
						variant='contained'
						sx={{
							backgroundColor: 'status.blue.bg',
							color: 'status.blue.text',
							border: '0.5px solid',
							borderColor: 'primary.main',
							boxShadow: 'none',
							'&:hover': {
								backgroundColor: 'status.blue.hover',
								boxShadow: 'none',
							},
						}}
					>
						Create Alert
					</Button>
				</Box>
			</form>
		</>
	);
};

CustomizeReportAlertForm.propTypes = {
	selectedAlert: PropTypes.object,
	reportId: PropTypes.string.isRequired,
};

export default CustomizeReportAlertForm;
